import { apiSlice } from './apiSlice'
import type { PnLRow } from '../../pages/dashboard/pnl/PnLTable'

// ===================== dashboardCashflowsApi =====================
/**
 * Cashflows Forecast dashboard data — `GET /api/v1/dashboard/cashflows`.
 *
 * Mirrors FastAPI `CashflowsDashboardResponse` (camelCase aliases). BE returns
 * DATA ROWS only; column/format config lives in `pages/dashboard/cashflows`.
 * Source: Report DB (`DATABASE_URL_REPORT`), ported 1:1 from the PBI page
 * "Cashflows Forecast". No params — the PBI page filter is a fixed relative
 * window (today .. today+49) computed server-side.
 */
export interface CashflowsDashboardResponse {
  /** Week-of-year of today (e.g. "2026-24") — header card. */
  currentWoy?: string | null
  /** Page window bounds, ISO `YYYY-MM-DD` (today .. today+49). */
  windowFrom?: string | null
  windowTo?: string | null
  /** Latest FX upload date inside the window (null = none yet today). */
  fxDate?: string | null
  fxRates: PnLRow[]
  /** Latest LRC price snapshot date (last 4 days). */
  marketDate?: string | null
  marketPrices: PnLRow[]
  cashAtBanks: PnLRow[]
  creditFacilities: PnLRow[]
  usdExposure: PnLRow[]
  /** Ordered woy column labels of the forecast matrix. */
  weeks: string[]
  /** Forecast rows: label/unit/bold/italic/red + w0..wN keyed by `weeks` index. */
  forecast: PnLRow[]
}

const dashboardCashflowsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCashflowsDashboard: builder.query<CashflowsDashboardResponse, void>({
      query: () => ({ url: '/dashboard/cashflows' }),
    }),
  }),
})

export const { useGetCashflowsDashboardQuery } = dashboardCashflowsApi

// ===================== dashboardFuturesPositionApi =====================
/**
 * Futures Position dashboard data — `GET /api/v1/dashboard/futures-position`.
 *
 * Mirrors FastAPI `FuturesPositionDashboardResponse` (camelCase aliases). BE
 * returns DATA ROWS only; column/format config lives in
 * `pages/dashboard/futuresPosition/mockData.ts`. The "Total lot" pivot has
 * dynamic flag columns, so the payload carries `flags` + nested per-flag cells
 * (`values[flag] = {buy, sell}`) that the FE flattens into column keys.
 * Source: Report DB (`DATABASE_URL_REPORT`).
 */
export interface TotalLotRow {
  fullSymbol: string
  values: Record<string, { buy: number | null; sell: number | null }>
  isTotal?: boolean
}

export interface FuturesPositionDashboardResponse {
  londonRobusta: PnLRow[]
  newyorkArabica: PnLRow[]
  futuresPosition: PnLRow[]
  totalLot: { flags: string[]; rows: TotalLotRow[] }
  /** PBI card `report_date` = MIN(max selected date, today). ISO `YYYY-MM-DD`. */
  reportDate?: string | null
}

export interface FuturesPositionDashboardQuery {
  /** ISO date `YYYY-MM-DD`; omitted = no lower bound. */
  fromDate?: string
  /** ISO date `YYYY-MM-DD`; omitted = no upper bound. */
  toDate?: string
}

const dashboardFuturesPositionApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getFuturesPositionDashboard: builder.query<
      FuturesPositionDashboardResponse,
      FuturesPositionDashboardQuery
    >({
      query: ({ fromDate, toDate } = {}) => ({
        url: '/dashboard/futures-position',
        params: {
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
        },
      }),
    }),
  }),
})

export const { useGetFuturesPositionDashboardQuery } = dashboardFuturesPositionApi

// ===================== dashboardInventoryApi =====================
/**
 * Inventory dashboard data — `GET /api/v1/dashboard/inventory`.
 *
 * Mirrors FastAPI `InventoryDashboardResponse` (camelCase aliases). BE returns
 * DATA ROWS only; column/format config lives in `pages/dashboard/inventory/`.
 * Source: Report DB (`DATABASE_URL_REPORT`). Spec: plan/Inventory_Dashboard_BE_Proposal.md.
 *
 * No date params: the PBI page "Inventory" has no date slicer — point-in-time.
 * Row shape dùng chung `LongShortRow` (values[warehouse][measureKey]).
 */
export interface InventoryDashboardResponse {
  /** PBI report_date card — ISO `YYYY-MM-DD`. */
  reportDate: string | null
  /** Ordered column groups: 4 warehouse (page filter PBI) + "Total" cuối. */
  warehouses: string[]
  /** Rows: category (Grade subtotal) → code → grand Total. Measures:
   *  receiveSp, issue, receivePro, shipped, stock. */
  rows: LongShortRow[]
}

const dashboardInventoryApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getInventoryDashboard: builder.query<InventoryDashboardResponse, void>({
      query: () => ({ url: '/dashboard/inventory' }),
    }),
  }),
})

export const { useGetInventoryDashboardQuery } = dashboardInventoryApi

// ===================== dashboardLongShortApi =====================
/**
 * Long-Short dashboard data — `GET /api/v1/dashboard/long-short`.
 *
 * Mirrors FastAPI `LongShortDashboardResponse` (camelCase aliases). BE returns
 * DATA ROWS only; column/format config lives in `pages/dashboard/longShort/`.
 * Source: Report DB (`DATABASE_URL_REPORT`). Spec: plan/LongShort_Dashboard_BE_Proposal.md.
 *
 * No date params: the PBI page has no date slicer — the source views are
 * point-in-time (months before the current one pre-aggregated into "<YYYY-M").
 */

/** One matrix row. `values[monthCate][measureKey]` — missing bucket/measure = blank cell. */
export interface LongShortRow {
  category: string
  code: string | null
  rowType: 'category' | 'code' | 'total'
  values: Record<string, Record<string, number | null>>
}

export interface LongShortDashboardResponse {
  /** PBI report_date card — ISO `YYYY-MM-DD`. */
  reportDate: string | null
  /** Ordered month buckets ("<2026-6", "2026-6", …) → dynamic top-tier header. */
  monthCates: string[]
  /** Level 1 — "Gross Long-Short (Kg)": purchase, sale, monthlyPs, cumulative. */
  gross: LongShortRow[]
  /** Level 2 — "Net Long-Short (Kg)": stock, unallocPurchase, unallocSale,
   *  allocUnship, shipped, netMonthlyPs, cumulative. */
  net: LongShortRow[]
}

const dashboardLongShortApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getLongShortDashboard: builder.query<LongShortDashboardResponse, void>({
      query: () => ({ url: '/dashboard/long-short' }),
    }),
  }),
})

export const { useGetLongShortDashboardQuery } = dashboardLongShortApi

// ===================== dashboardPnlApi =====================
/**
 * Commercial Position (PnL) dashboard data — `GET /api/v1/dashboard/pnl`.
 *
 * Mirrors FastAPI `PnLDashboardResponse` (camelCase aliases). BE returns DATA
 * ROWS only; column/format config lives in `pages/dashboard/pnl/pnlColumns.ts`.
 * Source: Report DB (`DATABASE_URL_REPORT`). Spec: plan/PBI_Dashboard.md.
 */
export interface PnLDashboardResponse {
  purchases: PnLRow[]
  sales: PnLRow[]
  futures: PnLRow[]
  inventory: PnLRow[]
  supplierM2M: PnLRow[]
  longShort: PnLRow[]
  m2mPnl: PnLRow[]
  /** Latest two `capture_pnl_daily` snapshot dates → dynamic M2M column labels. */
  m2mTodayDate?: string | null
  m2mPrevDate?: string | null
  physicalPrice: PnLRow[]
  exchangeRate: PnLRow[]
  marketPrice: PnLRow[]
}

export interface PnLDashboardQuery {
  /** ISO date `YYYY-MM-DD`; omitted = no lower bound. */
  fromDate?: string
  /** ISO date `YYYY-MM-DD`; omitted = no upper bound. */
  toDate?: string
}

const dashboardPnlApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPnLDashboard: builder.query<PnLDashboardResponse, PnLDashboardQuery>({
      query: ({ fromDate, toDate } = {}) => ({
        url: '/dashboard/pnl',
        params: {
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
        },
      }),
    }),
  }),
})

export const { useGetPnLDashboardQuery } = dashboardPnlApi

// ===================== dashboardRiskManagementApi =====================
/** Mirrors FastAPI `SupplierExposureRow` (camelCase). One supplier row of the
 *  Risk Management ▸ Supplier's Exposure matrix (numbers are MT/USD, blank → null).
 *  The trailing grand-total row has `isTotal: true` — BE re-evaluates the
 *  conditional Room-to-buy measures over the whole dataset, so never re-sum
 *  client-side. */
export interface SupplierExposureRow {
  supplierName: string | null
  purchaseMt: number | null
  toReceiveMt: number | null
  toReceiveUsd: number | null
  outrightMt: number | null
  outrightUsd: number | null
  ptbfMt: number | null
  ptbfUsd: number | null
  rm1NpeBalanceUnpaid: number | null
  rm1Cumulative: number | null
  rm2PtbfBalance: number | null
  rm2Cumulative: number | null
  rm3CargoOffloaded: number | null
  rm3Cumulative: number | null
  rm4CargoInCompound: number | null
  rm4Cumulative: number | null
  exposureLimitMt: number | null
  exposureLimitUsd: number | null
  roomMt: number | null
  roomUsdWithRm2: number | null
  roomUsdWithoutMitigation: number | null
  isTotal: boolean
}

export interface SupplierExposureResponse {
  rows: SupplierExposureRow[]
  /** ISO date of the PBI header card (`min(max d_date, today)`). */
  reportDate: string | null
}

export const riskManagementApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSupplierExposure: builder.query<SupplierExposureResponse, void>({
      query: () => '/dashboard/risk-management/supplier-exposure',
    }),
  }),
})

export const { useGetSupplierExposureQuery } = riskManagementApi

// ===================== reportCashflowApi =====================
/**
 * Report › Inputs › Cash Flows — Access `frmReportCashFlowMain` hub.
 * Each "Go to Page" entry reads a `v_report_cashflow_*` view; the dialogs write
 * the mapping / input-value base tables.
 */

export interface CashflowListResponse<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

export interface WeekColumn {
  weekCode: string
  weekName: string
  woy: string
  startDate: string | null
  endDate: string | null
}

export interface InputDetail {
  qualityCode: string | null
  inputType: string | null
  orderBy: string | null
  category: string | null
  description: string | null
  priceFromG2: number | null
  week00: number | null
  week01: number | null
  week02: number | null
  week03: number | null
  week04: number | null
  week05: number | null
  week06: number | null
  week07: number | null
  inputName: string | null
}

export interface BankBalance {
  bankAccountNumber: string | null
  bankName: string | null
  openAmountOc: number | null
  openAmount: number | null
  debitAmountOc: number | null
  debitAmount: number | null
  creditAmountOc: number | null
  creditAmount: number | null
  closeAmountOc: number | null
  closeAmount: number | null
  bankFx: number | null
  bankBranchName: string | null
  isSudung: string | null
  description: string | null
  address: string | null
  reloadDatetime: string | null
}

export interface MapCp {
  checkType: string | null
  isMap: string | null
  erpCode: string | null
  erpShortName: string | null
  erpName: string | null
  erpVat: string | null
  erpAddress: string | null
  misaCode: string | null
  misaName: string | null
  misaVat: string | null
  isVender: string | null
  isCustomer: string | null
  isEmployee: string | null
  hadPcontract: string | null
  hadScontract: string | null
  misaLstInvoice: string | null
  misaNgoaile: string | null
  misaId: string | null
}

export interface MapService {
  isMapped: string | null
  serviceCode: string | null
  serviceTypeName: string | null
  serviceName: string | null
  misaCode: string | null
  misaName: string | null
  misaAddress: string | null
  misaVatCode: string | null
  tlcPic: string | null
  tel: string | null
  misaId: string | null
  address: string | null
  taxNo: string | null
  note: string | null
  serviceType: string | null
}

export interface MisaObject {
  misaCode: string | null
  misaName: string | null
  misaAddress: string | null
  misaVatCode: string | null
  erpCode: string | null
  erpName: string | null
  mappedTxt: string | null
  misaId: string | null
}

export interface MapMuaHang {
  mappedTxt: string | null
  sohoadonCheckdate: string | null
  erpCp: string | null
  erpCpName: string | null
  erpMisaCode: string | null
  erpTabName: string | null
  erpInvNo: string | null
  invoiceDate: string | null
  invoiceAmount: number | null
  erpInvNote: string | null
  tabName: string | null
  nhacungcap: string | null
  sochungtu: string | null
  sohoadon: string | null
  ngayhoadon: string | null
  thanhtienQuydoi: number | null
  thanhtoanQuydoi: number | null
  loaitien: string | null
  tygia: number | null
  refid: string | null
  paydataRequested: number | null
  paydataPaid: number | null
  contractNo: string | null
  journalMemo: string | null
  misaMapId: number | null
}

export interface MapBanHang {
  isMapped: string | null
  contractNo: string | null
  contractDate: string | null
  erpInv: string | null
  buyersRef: string | null
  cpCode: string | null
  shortName: string | null
  accountObjectCode: string | null
  tabName: string | null
  sochungtu: string | null
  misaContract: string | null
  contractExtract: string | null
  invExtract: string | null
  misaSohoadon: string | null
  dienGiai: string | null
  contractType: string | null
  refId: string | null
  erpInvDate: string | null
  erpInvPriceCustomer: number | null
  erpInvPriceCustoms: number | null
  erpInvAmountCustomer: number | null
  erpInvAmountCustoms: number | null
  misaMapId: number | null
}

export interface MapBanHangDiff {
  customsInvNo: string | null
  contractNo: string | null
  erpKhachhang: string | null
  misaLstCt: string | null
  misaKhachhang: string | null
}

export interface LookupItem {
  value: string
  label: string
  description?: string | null
}
export interface LookupResponse {
  items: LookupItem[]
  total: number
}

export interface InputValueWrite {
  inputType: string
  qualityCode: string | null
  values: Record<string, number | null>
}
export interface MapCpWrite {
  cpCode: string
  misaId: string | null
}
export interface MapServiceWrite {
  serviceCode: string
  misaId: string | null
}
export interface MapMuaHangWrite {
  misaMapId: number | null
  erpCp: string | null
  erpInvNo: string | null
  erpInvDate: string | null
  erpMisaCode: string | null
  misaCp: string | null
  misaInvNo: string | null
  misaInvDate: string | null
  misaRef: string | null
  misaObjectCode: string | null
}
export interface MapBanHangWrite {
  misaMapId: number | null
  erpInvNo: string | null
  erpContractNo: string | null
  erpCpCode: string | null
  misaCpCode: string | null
  misaRefId: string | null
  contractExtract: string | null
  invExtract: string | null
}

const TAG = 'ReportCashflow' as const

/** Filter Date range (Report Inputs header) → muahang/banhang list params. */
export interface DateRangeArg {
  startDate?: string | null
  endDate?: string | null
}
const dateParams = (arg: DateRangeArg | void): Record<string, string> | undefined => {
  if (!arg || !arg.startDate || !arg.endDate) return undefined
  return { startDate: arg.startDate, endDate: arg.endDate }
}

const reportCashflowApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    getCashflowWeeks: b.query<WeekColumn[], void>({
      query: () => '/report-cashflow/weeks',
      providesTags: [TAG],
    }),
    getCashflowInput: b.query<CashflowListResponse<InputDetail>, { page: string }>({
      query: ({ page }) => ({ url: '/report-cashflow/input', params: { page } }),
      providesTags: [TAG],
    }),
    getCashflowBankBalance: b.query<CashflowListResponse<BankBalance>, void>({
      query: () => '/report-cashflow/bank-balance',
      providesTags: [TAG],
    }),
    getCashflowMapCp: b.query<CashflowListResponse<MapCp>, void>({
      query: () => '/report-cashflow/map-cp',
      providesTags: [TAG],
    }),
    getCashflowMapCpNeed: b.query<CashflowListResponse<MapCp>, void>({
      query: () => '/report-cashflow/map-cp/need',
      providesTags: [TAG],
    }),
    getCashflowMapCpDup: b.query<CashflowListResponse<MapCp>, void>({
      query: () => '/report-cashflow/map-cp/dup',
      providesTags: [TAG],
    }),
    getCashflowMapService: b.query<CashflowListResponse<MapService>, void>({
      query: () => '/report-cashflow/map-service',
      providesTags: [TAG],
    }),
    getCashflowMapServiceDup: b.query<CashflowListResponse<MapService>, void>({
      query: () => '/report-cashflow/map-service/dup',
      providesTags: [TAG],
    }),
    getCashflowMisaObject: b.query<CashflowListResponse<MisaObject>, void>({
      query: () => '/report-cashflow/misa-object',
      providesTags: [TAG],
    }),
    getCashflowMapMuaHang: b.query<CashflowListResponse<MapMuaHang>, DateRangeArg | void>({
      query: (arg) => ({ url: '/report-cashflow/map-muahang', params: dateParams(arg) }),
      providesTags: [TAG],
    }),
    getCashflowMapMuaHangNeed: b.query<CashflowListResponse<MapMuaHang>, DateRangeArg | void>({
      query: (arg) => ({ url: '/report-cashflow/map-muahang/need', params: dateParams(arg) }),
      providesTags: [TAG],
    }),
    getCashflowMisaMuaHang: b.query<{ items: Record<string, unknown>[] }, DateRangeArg | void>({
      query: (arg) => ({ url: '/report-cashflow/map-muahang/misa', params: dateParams(arg) }),
      providesTags: [TAG],
    }),
    getCashflowMapBanHang: b.query<CashflowListResponse<MapBanHang>, DateRangeArg | void>({
      query: (arg) => ({ url: '/report-cashflow/map-banhang', params: dateParams(arg) }),
      providesTags: [TAG],
    }),
    getCashflowMapBanHangDiff: b.query<CashflowListResponse<MapBanHangDiff>, void>({
      query: () => '/report-cashflow/map-banhang/diff',
      providesTags: [TAG],
    }),
    getCashflowMisaBanHang: b.query<{ items: Record<string, unknown>[] }, DateRangeArg | void>({
      query: (arg) => ({ url: '/report-cashflow/map-banhang/misa', params: dateParams(arg) }),
      providesTags: [TAG],
    }),
    // Lookups (lazy — dialogs)
    getCashflowLookup: b.query<LookupResponse, { kind: string; q?: string }>({
      query: ({ kind, q }) => ({ url: `/report-cashflow/lookups/${kind}`, params: q ? { q } : undefined }),
    }),
    getCashflowDetailLookup: b.query<{ items: Record<string, unknown>[] }, { kind: string; q?: string }>({
      query: ({ kind, q }) => ({ url: `/report-cashflow/detail-lookups/${kind}`, params: q ? { q } : undefined }),
    }),
    // Access: DLookUp price_from_g2 by quality code (Input Value dialog, live).
    getCashflowPriceFromG2: b.query<{ priceFromG2: number | null }, string>({
      query: (qualityCode) => ({ url: '/report-cashflow/price-from-g2', params: { qualityCode } }),
    }),
    // One quality's saved week values — used by the Input dialog to reload the KG
    // cells when the user picks a Quality Code. `item` is null if no row yet.
    getCashflowInputDetail: b.query<{ item: InputDetail | null }, { inputType: string; qualityCode: string }>({
      query: ({ inputType, qualityCode }) => ({ url: '/report-cashflow/input/detail', params: { inputType, qualityCode } }),
    }),
    // Mutations
    saveCashflowInput: b.mutation<{ message: string }, InputValueWrite>({
      query: (body) => ({ url: '/report-cashflow/input', method: 'POST', body }),
      invalidatesTags: [TAG],
    }),
    saveCashflowMapCp: b.mutation<{ message: string }, MapCpWrite>({
      query: (body) => ({ url: '/report-cashflow/map-cp', method: 'POST', body }),
      invalidatesTags: [TAG],
    }),
    saveCashflowMapService: b.mutation<{ message: string }, MapServiceWrite>({
      query: (body) => ({ url: '/report-cashflow/map-service', method: 'POST', body }),
      invalidatesTags: [TAG],
    }),
    saveCashflowMapMuaHang: b.mutation<{ message: string }, MapMuaHangWrite>({
      query: (body) => ({ url: '/report-cashflow/map-muahang', method: 'POST', body }),
      invalidatesTags: [TAG],
    }),
    deleteCashflowMapMuaHang: b.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/report-cashflow/map-muahang/${id}`, method: 'DELETE' }),
      invalidatesTags: [TAG],
    }),
    saveCashflowMapBanHang: b.mutation<{ message: string }, MapBanHangWrite>({
      query: (body) => ({ url: '/report-cashflow/map-banhang', method: 'POST', body }),
      invalidatesTags: [TAG],
    }),
    deleteCashflowMapBanHang: b.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/report-cashflow/map-banhang/${id}`, method: 'DELETE' }),
      invalidatesTags: [TAG],
    }),
  }),
})

export const {
  useGetCashflowWeeksQuery,
  useGetCashflowInputQuery,
  useGetCashflowBankBalanceQuery,
  useGetCashflowMapCpQuery,
  useGetCashflowMapCpNeedQuery,
  useGetCashflowMapCpDupQuery,
  useGetCashflowMapServiceQuery,
  useGetCashflowMapServiceDupQuery,
  useGetCashflowMisaObjectQuery,
  useGetCashflowMapMuaHangQuery,
  useGetCashflowMapMuaHangNeedQuery,
  useGetCashflowMisaMuaHangQuery,
  useGetCashflowMapBanHangQuery,
  useGetCashflowMapBanHangDiffQuery,
  useGetCashflowMisaBanHangQuery,
  useGetCashflowLookupQuery,
  useGetCashflowDetailLookupQuery,
  useGetCashflowPriceFromG2Query,
  useLazyGetCashflowInputDetailQuery,
  useSaveCashflowInputMutation,
  useSaveCashflowMapCpMutation,
  useSaveCashflowMapServiceMutation,
  useSaveCashflowMapMuaHangMutation,
  useDeleteCashflowMapMuaHangMutation,
  useSaveCashflowMapBanHangMutation,
  useDeleteCashflowMapBanHangMutation,
} = reportCashflowApi


// ===================== reportExposureApi =====================
/** One row of `public.v_report_exposure_limit` (Exposure Limit datasheet). */
export interface ExposureLimit {
  id: number | null
  code: string | null
  name: string | null
  shortName: string | null
  limitDate: string | null
  quantityLimitMt: number | null
  amountLimitUsd: number | null
  limitNote: string | null
  isApproved: number | null
  approvedTxt: string | null
  dateUpdated: string | null
  personUpdated: string | null
}

export interface ExposureLimitListResponse {
  items: ExposureLimit[]
  total: number
  page: number
  pageSize: number
}

/** Edit Limit dialog payload → INSERT a new limit row. */
export interface ExposureLimitWrite {
  code: string
  limitDate: string | null
  quantityLimitMt: number | null
  amountLimitUsd: number | null
  limitNote: string | null
}

export interface ExposureApproveItem {
  id: number
  quantityLimitMt: number | null
  amountLimitUsd: number | null
  limitNote: string | null
}

interface ExposureLookupItem {
  value: string
  label: string
}
interface ExposureLookupResponse {
  items: ExposureLookupItem[]
  total: number
}

const reportExposureApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getExposureLimits: builder.query<ExposureLimitListResponse, { page: number; pageSize: number }>({
      query: ({ page, pageSize }) => ({ url: '/report-exposure', params: { page, pageSize } }),
      providesTags: ['ReportExposure'],
    }),
    getExposurePending: builder.query<ExposureLimit[], void>({
      query: () => '/report-exposure/pending',
      providesTags: ['ReportExposure'],
    }),
    getExposureCanApprove: builder.query<{ canApprove: boolean }, void>({
      query: () => '/report-exposure/can-approve',
    }),
    getSpotMonth: builder.query<{ futuresMonth: string | null }, void>({
      query: () => '/report-exposure/spot-month',
      providesTags: ['ReportExposure'],
    }),
    getFuturesMonthOptions: builder.query<ExposureLookupResponse, void>({
      query: () => '/report-exposure/lookups/futures-month',
    }),
    createExposureLimit: builder.mutation<{ message: string }, ExposureLimitWrite>({
      query: (body) => ({ url: '/report-exposure', method: 'POST', body }),
      invalidatesTags: ['ReportExposure'],
    }),
    approveExposureLimits: builder.mutation<{ message: string }, { items: ExposureApproveItem[] }>({
      query: (body) => ({ url: '/report-exposure/approve', method: 'POST', body }),
      invalidatesTags: ['ReportExposure'],
    }),
    setSpotMonth: builder.mutation<{ message: string }, { futuresMonth: string }>({
      query: (body) => ({ url: '/report-exposure/spot-month', method: 'POST', body }),
      invalidatesTags: ['ReportExposure'],
    }),
  }),
})

export const {
  useGetExposureLimitsQuery,
  useGetExposurePendingQuery,
  useGetExposureCanApproveQuery,
  useGetSpotMonthQuery,
  useGetFuturesMonthOptionsQuery,
  useCreateExposureLimitMutation,
  useApproveExposureLimitsMutation,
  useSetSpotMonthMutation,
} = reportExposureApi
